import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tickets } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { Checkbox, Select, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { TICKETS_KEY, personName, ticketErrorText, useLookups, useTicketSettings } from '../lib/data';
import { missingRequired, normaliseValue } from '../lib/fields';
import { FieldInput } from './FieldInput';

export interface CreateTicketPrefill {
  subject?: string;
  conversationId?: string;
  channel?: string;
  contact?: { id?: string; ref?: string; name?: string };
}

/**
 * A new ticket: from the queue, or from an inbox conversation (which links it and names the client). The type's
 * defaults fill the team and priority until somebody picks their own; its required fields are asked for here.
 */
export function CreateTicketModal({ opened, onClose, prefill, onCreated }: {
  opened: boolean;
  onClose: () => void;
  prefill?: CreateTicketPrefill;
  onCreated?: (ticket: tickets.Ticket) => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const { user } = useAuth();
  const lk = useLookups();
  const settingsQ = useTicketSettings();
  const types = useMemo(() => lk.types.filter((x) => x.active).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)), [lk.types]);

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [typeId, setTypeId] = useState('');
  const [priorityId, setPriorityId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [assignee, setAssignee] = useState('');
  const [clientName, setClientName] = useState('');
  const [fields, setFields] = useState<Record<string, unknown>>({});
  const [notify, setNotify] = useState(true);
  const [touched, setTouched] = useState({ team: false, priority: false });
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setSubject(prefill?.subject ?? '');
    setDescription('');
    setTypeId('');
    setPriorityId('');
    setTeamId('');
    setAssignee('');
    setClientName(prefill?.contact?.name ?? '');
    setFields({});
    setTouched({ team: false, priority: false });
    setTried(false);
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  // Read with care (8 October 2026): this dialog sits closed inside the inbox, so a settings answer without
  // notify_client must not take the whole inbox down.
  useEffect(() => { setNotify(settingsQ.data?.data?.notify_client?.created ?? true); }, [settingsQ.data, opened]);

  // The first type, once they have loaded.
  const type = types.find((x) => x.id === typeId) ?? null;
  useEffect(() => { if (opened && !typeId && types[0]) setTypeId(types[0].id); }, [opened, types, typeId]);

  // The type's defaults, unless somebody chose their own.
  useEffect(() => {
    if (!type) return;
    if (!touched.team) setTeamId(type.default_team_id ?? '');
    if (!touched.priority) setPriorityId(type.default_priority_id ?? lk.priorities.find((p) => p.is_default)?.id ?? '');
  }, [type?.id, lk.priorities]); // eslint-disable-line react-hooks/exhaustive-deps

  const required = (type?.fields ?? []).filter((f) => f.required);
  const missing = missingRequired(required, fields);
  const problem = !subject.trim() ? 'subject' : missing.length ? 'fields' : null;

  const create = useMutation({
    mutationFn: () => {
      const values: Record<string, unknown> = {};
      for (const f of required) values[f.key] = normaliseValue(f.type, fields[f.key]);
      const contact = prefill?.contact
        ? { ...prefill.contact, name: prefill.contact.name || undefined }
        : clientName.trim() ? { name: clientName.trim() } : undefined;
      return tickets.createTicket({
        subject: subject.trim(),
        description: description.trim() || undefined,
        type_id: typeId || undefined,
        priority_id: priorityId || undefined,
        team_id: teamId || undefined,
        assignee_user_id: assignee || undefined,
        contact,
        conversation_id: prefill?.conversationId,
        channel: prefill?.channel,
        fields: required.length ? values : undefined,
        notify_client: prefill?.conversationId ? notify : false
      });
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: [TICKETS_KEY] });
      onCreated?.(res.data);
      onClose();
    }
  });

  function submit() {
    setTried(true);
    if (problem) return;
    create.mutate();
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t('tickets.create.title')} size="lg">
      <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <Stack gap="md" data-testid="create-ticket">
          <div data-guide="create-subject">
          <TextInput
            label={t('tickets.field.subject')}
            value={subject}
            onChange={(e) => setSubject(e.currentTarget.value)}
            required
            maxLength={200}
            error={tried && problem === 'subject' ? t('tickets.create.subjectRequired') : undefined}
            data-autofocus
          />
          </div>
          {!prefill?.contact && (
            <TextInput
              label={t('tickets.field.clientName')}
              value={clientName}
              onChange={(e) => setClientName(e.currentTarget.value)}
              maxLength={120}
              description={t('tickets.create.clientNameHint')}
            />
          )}
          <div data-guide="create-description">
          <Textarea
            label={t('common.description')}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            minRows={2}
            maxLength={4000}
          />
          </div>
          <Group gap="sm" grow wrap="wrap" align="flex-start" data-guide="create-type">
            <Select
              label={t('tickets.field.type')}
              value={typeId}
              onChange={(e) => { setTypeId(e.currentTarget.value); setFields({}); }}
              data={types.map((x) => ({ value: x.id, label: x.name }))}
            />
            <Select
              label={t('tickets.field.priority')}
              value={priorityId}
              onChange={(e) => { setPriorityId(e.currentTarget.value); setTouched((x) => ({ ...x, priority: true })); }}
              data={lk.priorities.map((p) => ({ value: p.id, label: p.name }))}
            />
          </Group>
          <Group gap="sm" grow wrap="wrap" align="flex-start" data-guide="create-team">
            <Select
              label={t('tickets.field.team')}
              value={teamId}
              onChange={(e) => { setTeamId(e.currentTarget.value); setTouched((x) => ({ ...x, team: true })); }}
              data={[{ value: '', label: t('tickets.noTeam') }, ...lk.teams.map((x) => ({ value: x.id, label: x.name }))]}
            />
            <Select
              label={t('tickets.field.assignee')}
              value={assignee}
              onChange={(e) => setAssignee(e.currentTarget.value)}
              data={[
                { value: '', label: t('tickets.unassigned') },
                ...(user ? [{ value: user.id, label: t('tickets.me') }] : []),
                ...lk.people.filter((p) => p.id !== user?.id).map((p) => ({ value: p.id, label: personName(p) }))
              ]}
            />
          </Group>

          {required.map((f) => (
            <FieldInput
              key={f.key}
              field={f}
              value={fields[f.key]}
              onChange={(v) => setFields((x) => ({ ...x, [f.key]: v }))}
              error={tried && missing.some((m) => m.key === f.key) ? t('tickets.field.requiredError') : undefined}
            />
          ))}

          {prefill?.conversationId && (
            <Checkbox
              label={t('tickets.create.notify')}
              description={t('tickets.create.notifyHint')}
              checked={notify}
              onChange={setNotify}
            />
          )}

          {create.error && <Alert color="error" role="alert">{ticketErrorText(t, create.error)}</Alert>}
          {types.length === 0 && !lk.loading && <Text role="caption" c="var(--rr-color-text-muted)">{t('tickets.create.noTypes')}</Text>}

          <Group gap="sm" justify="flex-end" data-guide="create-save">
            <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button type="submit" loading={create.isPending}>{t('tickets.create.submit')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
